import {
    faArrowProgress,
    faCircleX,
    faFileArrowDown,
    faFileCsv,
    faFilePdf,
    faPrint,
    faReceipt,
    faTag,
    faList,
} from "@fortawesome/pro-light-svg-icons"
import { useAppLocalization } from "../../../../localization/components/AppLocalizationProvider"
import { Order } from "../../../controlKitchenTypes"
import { DateTime } from "luxon"
import { LocalizedStrict } from "../../../../localization/components/AppLocalized"

import { printReceipt, saveReceiptAsFile, StandardTransactionResultResponse, TransactionResult } from "../productionApi"

import { useState } from "react"
import { useApiMutation } from "../../../../../core/api/useApiMutation"

import { FilteredStatuses } from "../meetingService/meetingServiceTypes"
import { useExportOrdersToCsv } from "./ExportFunctionOrder"
import { MRT_RowSelectionState } from "material-react-table"
import { UseMutateAsyncFunction } from "@tanstack/react-query"
import { enqueueSnackbar } from "notistack"
import StandardTransactionResultResponseModal from "./StandardTransactionResultResponseModal"
import { LoadingDialog } from "../../../../../shared/components/dialogs/LoadingDialog"
import LoadingLottie from "../assets/Loading-color-balls-jumping.json"
import { ConfirmModal } from "../../../../../shared/components/dialogs/ConfirmModal"
import {
    ActionBarItem,
    ActionBarMenu,
    ActionBarMenuItem,
    FloatingActionBar,
} from "../../../../../shared/components/table/FloatingActionBar"
import { ReceiptDialog } from "../../../../receipt/pages/ReceiptDialog"
import { useLabelPrinter } from "../../../../serviceCenter/report/home/hooks/useLabelPrinter"
import { LabelPrinterExtensionDialog } from "../../../../../shared/components/dialogs/LabelPrinterExtensionDialog"
import { GuestData } from "../../../../serviceCenter/report/external/brotherPrinter"

type FloatingOrderActionBarProps = {
    variant?: "default" | "pos"
    selectedRows: Order[]
    startDate: DateTime
    endDate: DateTime
    handleClearSelected: () => void
    setSelectedRows?: React.Dispatch<React.SetStateAction<MRT_RowSelectionState>>
    onStatusClick?: (e: React.MouseEvent<HTMLButtonElement>) => void
    onPrintDeliveryList?: () => void
    hidePrintActions?: boolean
}

export default function FloatingOrderActionBar({
    variant = "default",
    selectedRows,
    startDate,
    endDate,
    handleClearSelected,
    onStatusClick,
    onPrintDeliveryList,
    hidePrintActions = false,
}: FloatingOrderActionBarProps) {
    const { l10n } = useAppLocalization()
    const isPos = variant === "pos"
    const isDefault = variant === "default"
    const orderIds = selectedRows ? selectedRows.map((order) => order.id) : []

    const { isInstalledError, clearState, printGuestLabel } = useLabelPrinter()

    const handlePrintLabels = () => {
        const labels: GuestData[] = selectedRows.map((order) => ({
            visitorName: order.customer?.displayName || "",
            visitorCompany: order.customer?.userGroup?.name || order.accountingCustomerParty?.name || "",
            hostName: order.customer?.displayName || "",
        }))
        // for now to trigger print install modal
        printGuestLabel(labels[0])
    }

    const [isMultipleReceiptModal, setIsMultipleReceiptModal] = useState(false)

    const { mutateAsync: saveReceiptAsFileCall } = useApiMutation(saveReceiptAsFile, {})

    const handlePDFDownload = async () => {
        const response = await saveReceiptAsFileCall({ orderIds: orderIds })
        let filename = `Facilitynet-Receipt-${startDate}_${endDate}.pdf`
        const url = window.URL.createObjectURL(response)
        const link = document.createElement("a")
        link.href = url
        link.setAttribute("download", filename)
        document.body.appendChild(link)
        link.click()
        window.URL.revokeObjectURL(url)
        link.remove()
    }
    const { mutateAsync: callPrintReceipt } = useApiMutation(printReceipt, {
        async onSuccess(params, data, context) {
            enqueueSnackbar({
                variant: "success",
                message: l10n.getString("print-queued"),
            })
        },
    })

    const [orderStatusResponseResult, setOrderStatusResponseResult] = useState<StandardTransactionResultResponse>()
    const { mutateAsync: callRemovePrintReceiptfromQueue, isLoading } = useApiMutation(printReceipt, {
        async onSuccess(params, data, context) {
            setOrderStatusResponseResult(data)
        },
    })

    const exportSelectedOrders = useExportOrdersToCsv({ data: selectedRows, startDate, endDate })

    const [showConfirmModalForRemoveQue, setShowConfirmModalForRemoveQue] = useState(false)
    const [showConfirmModalForPrint, setShowConfirmModalForPrint] = useState(false)

    return (
        <>
            <FloatingActionBar selectedItemsLength={selectedRows.length} handleCloseActionBar={handleClearSelected}>
                {onStatusClick && (
                    <LocalizedStrict id="floating-action-button-order-status" attrs={{ title: true }}>
                        <ActionBarItem icon={faArrowProgress} title="Order status" onClick={onStatusClick} />
                    </LocalizedStrict>
                )}
                {!hidePrintActions && (
                    <LocalizedStrict id="floating-action-button-print" attrs={{ title: true }}>
                        <ActionBarMenu icon={faPrint} title="Print">
                            <LocalizedStrict
                                id={isPos ? "floating-action-button-print-receipt" : "floating-action-button-print"}
                                attrs={{ title: true }}
                            >
                                <ActionBarMenuItem
                                    icon={faReceipt}
                                    title="Print"
                                    onClick={() => setIsMultipleReceiptModal(true)}
                                />
                            </LocalizedStrict>
                            {isPos && (
                                <>
                                    <LocalizedStrict id="floating-action-button-print-label" attrs={{ title: true }}>
                                        <ActionBarMenuItem
                                            icon={faTag}
                                            title="Print label"
                                            onClick={() => handlePrintLabels()}
                                        />
                                    </LocalizedStrict>
                                    {onPrintDeliveryList && (
                                        <LocalizedStrict
                                            id="meeting-service-table-floating-action-bar-delivery-list"
                                            attrs={{ title: true }}
                                        >
                                            <ActionBarMenuItem
                                                icon={faList}
                                                title="Delivery list"
                                                onClick={onPrintDeliveryList}
                                            />
                                        </LocalizedStrict>
                                    )}
                                </>
                            )}
                            {isDefault && (
                                <LocalizedStrict id="export-button-remove-print-title" attrs={{ title: true }}>
                                    <ActionBarMenuItem
                                        icon={faCircleX}
                                        title="Remove from printer queue"
                                        onClick={() => setShowConfirmModalForRemoveQue(true)}
                                    />
                                </LocalizedStrict>
                            )}
                        </ActionBarMenu>
                    </LocalizedStrict>
                )}

                <LocalizedStrict id="floating-action-button-export" attrs={{ title: true }}>
                    <ActionBarMenu icon={faFileArrowDown} title="Export">
                        <LocalizedStrict id="export-button-export-as-pdf-title" attrs={{ title: true }}>
                            <ActionBarMenuItem
                                icon={faFilePdf}
                                title="Save as PDF"
                                onClick={() => handlePDFDownload()}
                            />
                        </LocalizedStrict>
                        <LocalizedStrict id="export-button-export-as-csv-title" attrs={{ title: true }}>
                            <ActionBarMenuItem
                                icon={faFileCsv}
                                title="Remove from printer queue"
                                onClick={() => exportSelectedOrders()}
                            />
                        </LocalizedStrict>
                    </ActionBarMenu>
                </LocalizedStrict>
            </FloatingActionBar>
            {isMultipleReceiptModal && (
                <ReceiptDialog
                    open={isMultipleReceiptModal}
                    onClose={() => setIsMultipleReceiptModal(false)}
                    receiptsForMultipleView={selectedRows}
                />
            )}
            {orderStatusResponseResult && (
                <StandardTransactionResultResponseModal
                    onClose={setOrderStatusResponseResult}
                    data={orderStatusResponseResult}
                />
            )}
            {showConfirmModalForRemoveQue && (
                <LocalizedStrict
                    id="production-orders-action-bar-confirm-queue-remove-dialog"
                    attrs={{ title: true, message: true, buttonLabelSubmit: true }}
                >
                    <ConfirmModal
                        open={showConfirmModalForRemoveQue}
                        onClose={() => setShowConfirmModalForRemoveQue(false)}
                        title="Remove from printer queue"
                        message="Are you sure that you want to remove selected items from the printer queue?"
                        buttonLabelSubmit="Remove"
                        handleClick={() => callRemovePrintReceiptfromQueue({ orderIds: orderIds })}
                    />
                </LocalizedStrict>
            )}
            {showConfirmModalForPrint && (
                <LocalizedStrict
                    id="production-orders-action-bar-confirm-print-dialog"
                    attrs={{ title: true, message: true, buttonLabelSubmit: true }}
                >
                    <ConfirmModal
                        title="Print receipts"
                        message="Are you sure you want to print the selected receipts?"
                        buttonLabelSubmit="Print"
                        open={showConfirmModalForPrint}
                        onClose={() => setShowConfirmModalForPrint(false)}
                        handleClick={() => callPrintReceipt({ orderIds: orderIds })}
                    />
                </LocalizedStrict>
            )}
            <LoadingDialog loading={isLoading} lottieAnim={LoadingLottie} />
            <LabelPrinterExtensionDialog open={isInstalledError} onClose={clearState} />
        </>
    )
}
